/**
 * Records composable following SOLID principles
 * Single Responsibility: Handles records/discovery-specific business logic
 * Refactored to use useRecordsData and useRecordsApi modules
 */
import { ref, computed, onMounted } from '@vue/composition-api';
import { useGamePage } from './useGamePage';
import { useRecordsData } from './records/useRecordsData';
import { useRecordsApi } from './records/useRecordsApi';
import { handleAsync } from '../services/ErrorHandling';

/**
 * Composable for records page functionality
 * @param {Object} props - Component props (memberLineId, groupId, etc.)
 * @returns {Object} Records state and methods
 */
export function useRecords(props) {
  // Base game page functionality
  const gamePageState = useGamePage('record', {
    bannerStyles: "top: 70px; left: 155px;"
  });

  // Records data management (Single Responsibility)
  const recordsDataState = useRecordsData();

  // API operations (Single Responsibility)
  const recordsApi = useRecordsApi();

  // Static images fallback
  const staticImages = {
    banner: require('@/assets/images/banner.png'),
    avatar: require('@/assets/images/banner-avatar.png'),
    icon: require('@/assets/images/yama/yama-icon.png'),
    background: require('@/assets/images/pg-bg.png'),
    contentBg: require('@/assets/images/content-bg.png')
  };

  // Content background for inner content area
  const contentBg = ref('');

  // Discovery types configuration
  const discoveryTypes = [
    { value: 'all', label: '全部' },
    {
      value: 'Exhibit',
      label: '證物',
      icon: require('@/assets/images/exhibit-icon.png')
    },
    {
      value: 'Position',
      label: '場景',
      icon: require('@/assets/images/soul-icon.png')
    },
    {
      value: 'Related',
      label: '相關人',
      icon: require('@/assets/images/related-icon.png')
    },
    {
      value: 'Timeline',
      label: '時間軸',
      icon: require('@/assets/images/time-icon.png')
    }
  ];

  /**
   * Override initViewData to handle records-specific setup
   */
  const initRecordsViewData = () => {
    const topicInfo = gamePageState.initViewData();

    if (topicInfo) {
      // Use topic info from store
      contentBg.value = topicInfo.question_block || staticImages.contentBg;
    } else {
      // Fallback to static images if store data not available
      gamePageState.bannerProps.imgSrc = staticImages.banner;
      gamePageState.bannerProps.content = '聖誕快樂';
      gamePageState.bannerProps.banner_avatar = staticImages.avatar;
      gamePageState.background.mainBg = staticImages.background;
      contentBg.value = staticImages.contentBg;
    }
  };

  /**
   * Load discovery records from API with error handling
   * Using Dependency Injection pattern with useRecordsApi
   */
  const loadRecordsData = async () => {
    const [error, data] = await handleAsync(
      recordsApi.loadRecordsData({
        groupId: props.groupId,
        memberLineId: props.memberLineId
      }),
      { operation: 'loadRecordsData', component: 'useRecords' }
    );

    if (error) {
      recordsDataState.discoveries.value = [];
      throw error;
    }

    // Initialize data using records data module
    recordsDataState.initRecordsData(data);

    return data;
  };

  /**
   * Filter discoveries by selected type
   * Computed from recordsDataState
   */
  const filteredDiscoveries = computed(() => {
    if (recordsDataState.selectedType.value === 'all') {
      return recordsDataState.discoveries.value;
    }
    return recordsDataState.discoveries.value.filter(discovery =>
      discovery.discoverable_type === recordsDataState.selectedType.value
    );
  });
  
  /**
   * Get discovery statistics
   * Uses recordsDataState for data access
   */
  const discoveryStats = computed(() => {
    const discoveries = recordsDataState.discoveries.value;
    const total = discoveries.length;
    const byType = discoveryTypes.reduce((stats, type) => {
      if (type.value === 'all') return stats;

      const count = discoveries.filter(d =>
        d.discoverable_type === type.value
      ).length;

      stats[type.value] = {
        count,
        label: type.label,
        icon: type.icon
      };

      return stats;
    }, {});

    // Count missing (discoverable_missing) + failed timelines (correct === false)
    const missing = discoveries.filter(d =>
      d.discoverable_missing || (d.discoverable_type === 'Timeline' && d.correct === false)
    ).length;
    const successful = total - missing;

    return {
      total,
      successful,
      missing,
      byType,
      successRate: total > 0 ? Math.round((successful / total) * 100) : 0
    };
  });
  
  /**
   * Get type label for display
   * @param {string} type - Discovery type
   * @returns {string} Display label
   */
  const getTypeLabel = (type) => {
    const typeMap = {
      'Exhibit': '證物',
      'Position': '場景',
      'Related': '相關人',
      'Timeline': '時間軸',
      'Testimony': '證詞'
    };
    return typeMap[type] || type;
  };
  
  /**
   * Get type icon for display
   * @param {string} type - Discovery type
   * @returns {string} Icon path
   */
  const getTypeIcon = (type) => {
    const iconMap = {
      'Exhibit': require('@/assets/images/exhibit-icon.png'),
      'Position': require('@/assets/images/soul-icon.png'),
      'Related': require('@/assets/images/related-icon.png'),
      'Timeline': require('@/assets/images/time-icon.png'),
      'Testimony': require('@/assets/images/related-icon.png')
    };
    return iconMap[type] || '';
  };
  
  /**
   * Check if discovery is missing/failed
   * @param {Object} discovery - Discovery object
   * @returns {boolean} True if missing
   */
  const isDiscoveryMissing = (discovery) => {
    return Boolean(discovery.discoverable_missing);
  };
  
  /**
   * Check if discovery is a failed timeline (wrong answer)
   * @param {Object} discovery - Discovery object
   * @returns {boolean} True if failed timeline
   */
  const isFailedTimeline = (discovery) => {
    return discovery.discoverable_type === 'Timeline' && discovery.correct === false;
  };
  
  /**
   * Get discovery status message
   * @param {Object} discovery - Discovery object
   * @returns {string} Status message
   */
  const getDiscoveryStatusMessage = (discovery) => {
    // Check failed timeline first (correct === false)
    if (isFailedTimeline(discovery)) {
      return '此時間線推理失敗，無法建立完整的事件順序。';
    }
    // Then check missing
    if (isDiscoveryMissing(discovery)) {
      return '此項目已被搜尋但未能找到，可能需要更多線索或是已被移除。';
    }
    return '';
  };
  
  /**
   * Set selected discovery type filter
   * Delegates to recordsDataState
   * @param {string} type - Discovery type to filter by
   */
  const setSelectedType = (type) => {
    recordsDataState.setSelectedType(type);
  };

  // Initialize on mount
  onMounted(async () => {
    initRecordsViewData();

    if (props.memberLineId) {
      await loadRecordsData();
    }

    gamePageState.completeInit();
  });

  return {
    // State from game page composable
    ...gamePageState,

    // Records-specific state from data module
    discoveries: recordsDataState.discoveries,
    selectedType: recordsDataState.selectedType,
    contentBg,
    discoveryTypes,

    // Computed
    filteredDiscoveries,
    discoveryStats,

    // Methods
    loadRecordsData,
    getTypeLabel,
    getTypeIcon,
    isDiscoveryMissing,
    isFailedTimeline,
    getDiscoveryStatusMessage,
    setSelectedType,

    // Override methods
    initViewData: initRecordsViewData
  };
}